// Pure helpers for the Purchase Entry (Goods Receipt) form.

export const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', 'Free', 'Free Size'];

export function createEmptyRow(sr) {
  return {
    sr, product_id: '', design_code: '', designSearch: '', color: '', size: '',
    qty: '', cost: 0, wsp_local: 0, wsp_all: 0, amount: 0,
    colors: [], sizes: [], sizeGroups: [], individualSizes: [],
    product: null, showDesignDropdown: false,
    linked_po_id: null, linked_po_item_id: null,
  };
}

// Colours available for a design: SKU colours (from previous PEs) unioned with
// colours tagged on catalogue images (image_colors values). This lets
// "forwarding" designs (no PE yet) still show colours in the PE form.
export function getDesignColors(product) {
  if (!product) return [];
  const skuColors = (product.skus || []).map(s => s.color);
  const imgColors = Object.values(product.image_colors || {}).filter(Boolean);
  return [...new Set([...skuColors, ...imgColors])];
}

// Build size groups by WSP for a product+color.
// When no SKUs exist for this colour (forwarding / first PE), fall back to the
// global enum sizes so the user can still enter quantities.
export function getSizeGroups(product, color, fallbackSizes) {
  if (!product || !color) return { groups: [], individualSizes: [] };
  const skus = product.skus?.filter(s => s.color === color) || [];

  const order = (s) => { const i = SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };

  if (skus.length === 0 && fallbackSizes && fallbackSizes.length > 0) {
    // No SKUs for this colour yet — use global enum sizes as individual options
    const sorted = [...fallbackSizes].sort((a, b) => order(a) - order(b));
    return { groups: [], individualSizes: sorted.map(s => ({ size: s, wsp: 0, mrp: 0 })) };
  }

  // Group by WSP (local) for PE
  const byPrice = {};
  skus.forEach(s => {
    const price = s.wsp || 0;
    if (!byPrice[price]) byPrice[price] = [];
    byPrice[price].push(s.size);
  });
  const groups = Object.entries(byPrice)
    .filter(([, sizes]) => sizes.length > 1)
    .map(([price, sizes]) => ({
      id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`,
      label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`,
      sizes, wsp: parseFloat(price), count: sizes.length,
      mrp: skus.find(s => s.size === sizes[0])?.mrp || parseFloat(price),
    }));
  const individualSizes = skus.map(s => ({ size: s.size, wsp: s.wsp || 0, mrp: s.mrp || 0 }));

  // For forwarding designs with partial SKUs, merge with fallback sizes
  if (fallbackSizes && fallbackSizes.length > 0) {
    const skuSizeSet = new Set(individualSizes.map(s => s.size));
    for (const sz of fallbackSizes) {
      if (!skuSizeSet.has(sz)) {
        individualSizes.push({ size: sz, wsp: 0, mrp: 0 });
      }
    }
    individualSizes.sort((a, b) => order(a.size) - order(b.size));
  }

  return { groups, individualSizes };
}

export function sortScannedRows(rows) {
  const filled = rows.filter(r => r.product_id);
  const empty = rows.filter(r => !r.product_id);
  filled.sort((a, b) => {
    const dA = (a.design_code || '').toUpperCase(), dB = (b.design_code || '').toUpperCase();
    if (dA !== dB) return dA.localeCompare(dB, undefined, { numeric: true });
    const cA = (a.color || '').toUpperCase(), cB = (b.color || '').toUpperCase();
    if (cA !== cB) return cA.localeCompare(cB);
    const sA = SIZE_ORDER.indexOf(a.size), sB = SIZE_ORDER.indexOf(b.size);
    return (sA === -1 ? 999 : sA) - (sB === -1 ? 999 : sB);
  });
  return [...filled.map((r, i) => ({ ...r, sr: i + 1 })), ...empty.map((r, i) => ({ ...r, sr: filled.length + i + 1 }))];
}

export function computeGrandTotals(rows) {
  const grandTotal = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const grandQty = rows.reduce((s, r) => {
    const q = parseInt(r.qty) || 0;
    if (r.size === 'both') {
      return s + (r.sizeGroups || []).reduce((sum, g) => sum + q * g.count, 0);
    }
    const grp = (r.sizeGroups || []).find(g => g.id === r.size);
    return s + (grp ? q * grp.count : q);
  }, 0);
  return { grandTotal, grandQty };
}
